import React from 'react';
import Error from '../Error';
import Spinner from '../Spinner';
import FlashMessage from './FlashMessage';

interface WithDataProps<T> {
    data: T;
}

/**
 * Result for actions on data to let LoadingWrapper handle them
 */
export interface ActionResult {
    result: 'success' | 'error',
    message?: string,
}

/**
 * Pass callable actions to loading wrapper for interaction with wrapper after
 * actions are triggered.
 * Actions passed to the wrapper will be passed on to the wrapper component as property named
 * "actions".
 */
export interface DataActions {
    [key: string]: (...args: any[]) => Promise<ActionResult>
}

/**
 * @see https://github.com/typescript-cheatsheets/react-typescript-cheatsheet/blob/master/HOC.md
 *
 * T is the type of data
 * P is the props of the wrapped component that is inferred
 * C is the actual interface of the wrapped component (used to grab defaultProps from it)
 */
export function withLoader<T, P extends WithDataProps<T>, C, A extends DataActions>(
    WrappedComponent: React.JSXElementConstructor<P> & C,
    loader: () => Promise<T>,
    actions: A,
    errorComponent?: React.JSXElementConstructor<{ actions: { [key: string]: () => any } }>,
    loadingComponent?: React.JSXElementConstructor<any>,
) {
    type Props = JSX.LibraryManagedAttributes<C, Omit<Omit<P, 'data'>, 'actions'>>;
    type State = {
        data: T | null,
        state: 'loading' | 'loaded' | 'error',
        messages: Array<ActionResult>,
    };

    return class WithData extends React.Component<Props, State> {
        constructor(props: Props) {
            super(props);
            this.state = {
                data: null,
                state: 'loading',
                messages: [],
            };
        }

        /**
         * Upon mounting initial data loading will be done
         */
        componentDidMount() {
            this.loadData();
        }


        /**
         * Wrap the actions with the handler before passing it to the component
         */
        getActionProps(): A {
            return Object.keys(actions).reduce((memo, action) => ({
                ...memo,
                [action]: (...args: any[]) => {
                    actions[action](...args)
                        .then(result => this.handleActionResult(result));
                },
            }), {}) as A;
        }

        /**
         * Add a message
         */
        addMessage(result: ActionResult) {
            if (!result.message) return;
            this.setState(prev => ({
                messages: [...prev.messages, result],
            }));
        }

        /**
         * Trigger data loading
         */
        loadData() {
            this.setState({ state: 'loading' });
            loader()
                .then(data => this.setState({ data, state: 'loaded' }))
                .catch(() => this.setState({ state: 'error' }));
        }

        /**
         * Handle a result received from an action
         */
        handleActionResult(result: ActionResult) {
            this.addMessage(result);
            if (result.result === 'success') {
                this.loadData();
            } else {
                this.setState({ state: 'loaded' });
            }
        }

        /**
         * Render all the messages in the message queue
         */
        renderMessages() {
            const { messages } = this.state;

            return messages.map(message => (
                <FlashMessage message={message.message || ''} type={message.result} />
            ));
        }

        /**
         * Render the current state
         */
        renderCurrent() {
            const { state } = this.state;

            // When loading we display a spinner or custom passed in loading component
            if (state === 'loading') {
                const LoadingComponent = loadingComponent || Spinner;
                return (<LoadingComponent />);
            }

            // When in error state we display the error component
            if (state === 'error') {
                const ErrorComponent = errorComponent || Error;
                return (
                    <ErrorComponent
                        actions={{ 'Erneut versuchen': () => this.loadData() }}
                    />
                );
            }

            const { data } = this.state;

            // When everything is fine we display the wrapped component
            return (
                <WrappedComponent
                    data={data}
                    actions={this.getActionProps()}
                    {...this.props as any}
                />
            );
        }

        /**
         * Combined component renderer
         */
        render() {
            return (
                <>
                    {this.renderCurrent()}
                    {this.renderMessages()}
                </>
            );
        }
    };
}
